import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  TextField,
  MenuItem,
  Button,
  Chip,
  CircularProgress,
  Card,
  CardContent,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
  Alert,
  LinearProgress,
  TablePagination,
  Autocomplete,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  FileDownload as ExcelIcon,
  PictureAsPdf as PdfIcon,
  Print as PrintIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  CheckCircle as SafeIcon,
  EventBusy as ExpiredIcon,
} from '@mui/icons-material';
import { useLanguage } from '../../contexts/LanguageContext';
import { PurchaseInvoice } from '../../types/purchaseInvoices.types';
import { purchaseInvoicesService } from '../../services/purchaseInvoices.service';
import { Warehouse } from '../../types/warehouses.types';
import { warehousesService } from '../../services/warehouses.service';
import { Category } from '../../types/items.types';
import { categoriesService } from '../../services/categories.service';
import { companyInfoService, CompanyInfo } from '../../services/companyInfo.service';
import { toast } from 'react-toastify';
import ExcelJS from 'exceljs';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const translations = {
  en: {
    title: 'Expiry Report',
    titleAr: 'تقرير انتهاء الصلاحية',
    overview: 'Overview',
    expiredItems: 'Expired Items',
    nearExpiry: 'Near Expiry (30 Days)',
    byCategory: 'By Category',
    byWarehouse: 'By Warehouse',
    category: 'Category',
    warehouse: 'Warehouse',
    allCategories: 'All Categories',
    allWarehouses: 'All Warehouses',
    expiryStatus: 'Expiry Status',
    allStatuses: 'All Statuses',
    itemCode: 'Item Code',
    itemName: 'Item',
    batchNumber: 'Batch #',
    expiryDate: 'Expiry Date',
    daysRemaining: 'Days Left',
    quantity: 'Qty',
    unitCost: 'Unit Cost',
    totalValue: 'Total Value',
    warehouseName: 'Warehouse',
    categoryName: 'Category',
    status: 'Status',
    expired: 'Expired',
    critical: 'Critical (7 Days)',
    warning: 'Warning (30 Days)',
    safe: 'Safe (>30 Days)',
    expiredCount: 'Expired Items',
    expiredValue: 'Expired Value',
    nearExpiryCount: 'Near Expiry',
    nearExpiryValue: 'Near Expiry Value',
    lossPercentage: 'Loss %',
    recommendation: 'Action',
    disposeNow: 'Dispose Immediately',
    urgentSale: 'Urgent Sale',
    promoteSale: 'Promote Sale',
    noAction: 'No Action Needed',
    refresh: 'Refresh',
    exportExcel: 'Export Excel',
    exportPdf: 'Export PDF',
    print: 'Print',
    noData: 'No expiry data found',
    loading: 'Loading...',
    kwd: 'KWD',
    totalItems: 'Total Items',
    itemsCount: 'Items',
  },
  ar: {
    title: 'تقرير انتهاء الصلاحية',
    titleAr: 'تقرير انتهاء الصلاحية',
    overview: 'نظرة عامة',
    expiredItems: 'منتهي الصلاحية',
    nearExpiry: 'قريب من الانتهاء (30 يوم)',
    byCategory: 'حسب الفئة',
    byWarehouse: 'حسب المخزن',
    category: 'الفئة',
    warehouse: 'المخزن',
    allCategories: 'جميع الفئات',
    allWarehouses: 'جميع المخازن',
    expiryStatus: 'حالة الصلاحية',
    allStatuses: 'جميع الحالات',
    itemCode: 'كود الصنف',
    itemName: 'الصنف',
    batchNumber: 'رقم الدفعة',
    expiryDate: 'تاريخ الانتهاء',
    daysRemaining: 'الأيام المتبقية',
    quantity: 'الكمية',
    unitCost: 'سعر الوحدة',
    totalValue: 'القيمة الإجمالية',
    warehouseName: 'المخزن',
    categoryName: 'الفئة',
    status: 'الحالة',
    expired: 'منتهي',
    critical: 'حرج (7 أيام)',
    warning: 'تحذير (30 يوم)',
    safe: 'آمن (>30 يوم)',
    expiredCount: 'عدد المنتهية',
    expiredValue: 'قيمة المنتهية',
    nearExpiryCount: 'قريب الانتهاء',
    nearExpiryValue: 'قيمة القريبة',
    lossPercentage: 'نسبة الخسارة %',
    recommendation: 'الإجراء',
    disposeNow: 'تخلص فوراً',
    urgentSale: 'بيع عاجل',
    promoteSale: 'عرض ترويجي',
    noAction: 'لا يوجد إجراء',
    refresh: 'تحديث',
    exportExcel: 'تصدير Excel',
    exportPdf: 'تصدير PDF',
    print: 'طباعة',
    noData: 'لا توجد بيانات صلاحية',
    loading: 'جاري التحميل...',
    kwd: 'د.ك',
    totalItems: 'إجمالي الأصناف',
    itemsCount: 'صنف',
  },
};

interface ExpiryItem {
  itemId: number;
  itemCode: string;
  itemName: string;
  itemNameAr: string;
  categoryId: number;
  categoryName: string;
  categoryNameAr: string;
  warehouseId: number;
  warehouseName: string;
  warehouseNameAr: string;
  batchNumber?: string;
  expiryDate: string;
  daysRemaining: number;
  quantity: number;
  unitCost: number;
  totalValue: number;
  status: 'expired' | 'critical' | 'warning' | 'safe';
  invoiceNumber: string;
  invoiceDate: string;
}

interface CategorySummary {
  categoryId: number;
  categoryName: string;
  categoryNameAr: string;
  totalItems: number;
  expiredItems: number;
  expiredValue: number;
  nearExpiryItems: number;
  nearExpiryValue: number;
}

interface WarehouseSummary {
  warehouseId: number;
  warehouseName: string;
  warehouseNameAr: string;
  totalItems: number;
  expiredItems: number;
  expiredValue: number;
  nearExpiryItems: number;
  nearExpiryValue: number;
}

const ExpiryReport: React.FC = () => {
  const { language } = useLanguage();
  const t = translations[language];
  const isRTL = language === 'ar';

  const [invoices, setInvoices] = useState<PurchaseInvoice[]>([]);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [companyInfo, setCompanyInfo] = useState<CompanyInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [currentTab, setCurrentTab] = useState(0);
  
  const [warehouseFilter, setWarehouseFilter] = useState<number | ''>('');
  const [categoryFilter, setCategoryFilter] = useState<number | ''>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [detailPage, setDetailPage] = useState(0);
  const [detailRowsPerPage, setDetailRowsPerPage] = useState(25);

  useEffect(() => {
    loadWarehouses();
    loadCategories();
    loadCompanyInfo();
    loadData();
  }, []);

  const loadCompanyInfo = async () => {
    try {
      const info = await companyInfoService.getCompanyInfo();
      setCompanyInfo(info);
    } catch (error) {
      console.error('Failed to load company info:', error);
    }
  };

  const loadWarehouses = async () => {
    try {
      const data = await warehousesService.getAll();
      setWarehouses(data);
    } catch (error) {
      console.error('Failed to load warehouses:', error);
    }
  };

  const loadCategories = async () => {
    try {
      const data = await categoriesService.getAll();
      setCategories(data);
    } catch (error) {
      console.error('Failed to load categories:', error);
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const data = await purchaseInvoicesService.getAll();
      setInvoices(data);
    } catch (error) {
      console.error('Failed to load invoices:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateDaysRemaining = (expiryDate: string): number => {
    const today = new Date();
    const expiry = new Date(expiryDate);
    const diffTime = expiry.getTime() - today.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  const getExpiryStatus = (daysRemaining: number): 'expired' | 'critical' | 'warning' | 'safe' => {
    if (daysRemaining < 0) return 'expired';
    if (daysRemaining <= 7) return 'critical';
    if (daysRemaining <= 30) return 'warning';
    return 'safe';
  };

  const getExpiryItems = (): ExpiryItem[] => {
    const items: ExpiryItem[] = [];
    
    invoices.forEach(invoice => {
      invoice.details.forEach(detail => {
        if (detail.expiryDate) {
          const daysRemaining = calculateDaysRemaining(detail.expiryDate);
          const status = getExpiryStatus(daysRemaining);
          
          items.push({
            itemId: detail.itemId,
            itemCode: detail.itemCode,
            itemName: detail.itemName,
            itemNameAr: detail.itemNameAr,
            categoryId: 0, // Will be populated from items
            categoryName: '',
            categoryNameAr: '',
            warehouseId: invoice.warehouseId,
            warehouseName: isRTL ? (invoice.warehouseNameAr || invoice.warehouseName || '-') : (invoice.warehouseName || '-'),
            warehouseNameAr: invoice.warehouseNameAr || '-',
            batchNumber: detail.batchNumber,
            expiryDate: detail.expiryDate,
            daysRemaining,
            quantity: detail.quantity,
            unitCost: detail.unitCost,
            totalValue: detail.quantity * detail.unitCost,
            status,
            invoiceNumber: invoice.invoiceNumber,
            invoiceDate: invoice.invoiceDate,
          });
        }
      });
    });

    return items;
  };

  const getFilteredItems = (): ExpiryItem[] => {
    let items = getExpiryItems();

    if (warehouseFilter) {
      items = items.filter(i => i.warehouseId === warehouseFilter);
    }

    if (statusFilter) {
      items = items.filter(i => i.status === statusFilter);
    }

    return items.sort((a, b) => a.daysRemaining - b.daysRemaining);
  };

  const getExpiredItems = (): ExpiryItem[] => {
    return getFilteredItems().filter(i => i.status === 'expired');
  };

  const getNearExpiryItems = (): ExpiryItem[] => {
    return getFilteredItems().filter(i => i.status === 'warning' || i.status === 'critical');
  };

  const getCategorySummary = (): CategorySummary[] => {
    const summary = new Map<number, CategorySummary>();
    const items = getFilteredItems();

    items.forEach(item => {
      if (!summary.has(item.categoryId)) {
        summary.set(item.categoryId, {
          categoryId: item.categoryId,
          categoryName: item.categoryName,
          categoryNameAr: item.categoryNameAr,
          totalItems: 0,
          expiredItems: 0,
          expiredValue: 0,
          nearExpiryItems: 0,
          nearExpiryValue: 0,
        });
      }

      const cat = summary.get(item.categoryId)!;
      cat.totalItems++;
      
      if (item.status === 'expired') {
        cat.expiredItems++;
        cat.expiredValue += item.totalValue;
      } else if (item.status === 'warning' || item.status === 'critical') {
        cat.nearExpiryItems++;
        cat.nearExpiryValue += item.totalValue;
      }
    });

    return Array.from(summary.values()).sort((a, b) => b.expiredValue - a.expiredValue);
  };

  const getWarehouseSummary = (): WarehouseSummary[] => {
    const summary = new Map<number, WarehouseSummary>();
    const items = getFilteredItems();

    items.forEach(item => {
      if (!summary.has(item.warehouseId)) {
        summary.set(item.warehouseId, {
          warehouseId: item.warehouseId,
          warehouseName: item.warehouseName,
          warehouseNameAr: item.warehouseNameAr,
          totalItems: 0,
          expiredItems: 0,
          expiredValue: 0,
          nearExpiryItems: 0,
          nearExpiryValue: 0,
        });
      }

      const wh = summary.get(item.warehouseId)!;
      wh.totalItems++;
      
      if (item.status === 'expired') {
        wh.expiredItems++;
        wh.expiredValue += item.totalValue;
      } else if (item.status === 'warning' || item.status === 'critical') {
        wh.nearExpiryItems++;
        wh.nearExpiryValue += item.totalValue;
      }
    });

    return Array.from(summary.values()).sort((a, b) => b.expiredValue - a.expiredValue);
  };

  const getStatistics = () => {
    const items = getFilteredItems();
    const expired = items.filter(i => i.status === 'expired');
    const nearExpiry = items.filter(i => i.status === 'warning' || i.status === 'critical');
    
    const expiredValue = expired.reduce((sum, i) => sum + i.totalValue, 0);
    const nearExpiryValue = nearExpiry.reduce((sum, i) => sum + i.totalValue, 0);
    const totalValue = items.reduce((sum, i) => sum + i.totalValue, 0);
    
    return {
      expiredCount: expired.length,
      expiredValue,
      nearExpiryCount: nearExpiry.length,
      nearExpiryValue,
      lossPercentage: totalValue > 0 ? (expiredValue / totalValue) * 100 : 0,
    };
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(isRTL ? 'ar-KW' : 'en-GB');
  };

  const formatCurrency = (amount: number) => {
    return amount.toFixed(3);
  };

  const getStatusColor = (status: string) => {
    switch(status) {
      case 'expired': return 'error';
      case 'critical': return 'error';
      case 'warning': return 'warning';
      case 'safe': return 'success';
      default: return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    switch(status) {
      case 'expired': return t.expired;
      case 'critical': return t.critical;
      case 'warning': return t.warning;
      case 'safe': return t.safe;
      default: return '';
    }
  };

  const getRecommendation = (daysRemaining: number) => {
    if (daysRemaining < 0) return t.disposeNow;
    if (daysRemaining <= 7) return t.urgentSale;
    if (daysRemaining <= 30) return t.promoteSale;
    return t.noAction;
  };

  const exportToExcel = async () => {
    try {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet(t.title);

      if (companyInfo?.logoBase64) {
        try {
          const logoImage = workbook.addImage({
            base64: companyInfo.logoBase64,
            extension: 'png',
          });
          worksheet.addImage(logoImage, {
            tl: { col: 0, row: 0 },
            ext: { width: 100, height: 80 }
          });
        } catch (err) {
          console.warn('Could not add logo:', err);
        }
      }

      worksheet.mergeCells('C1:K1');
      const companyCell = worksheet.getCell('C1');
      companyCell.value = isRTL ? companyInfo?.companyNameAr : companyInfo?.companyName;
      companyCell.font = { size: 20, bold: true, color: { argb: 'FFDC143C' } };
      companyCell.alignment = { horizontal: 'center', vertical: 'middle' };

      worksheet.mergeCells('A5:L5');
      const titleCell = worksheet.getCell('A5');
      titleCell.value = isRTL ? t.titleAr : t.title;
      titleCell.font = { size: 16, bold: true, color: { argb: 'FFFFFFFF' } };
      titleCell.alignment = { horizontal: 'center', vertical: 'middle' };
      titleCell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFDC143C' }
      };

      const data = getFilteredItems();
      const headers = [
        t.itemCode, t.itemName, t.batchNumber, t.expiryDate,
        t.daysRemaining, t.quantity, t.unitCost, t.totalValue,
        t.warehouseName, t.status, t.recommendation
      ];
      
      const headerRow = worksheet.getRow(8);
      headers.forEach((header, i) => {
        const cell = headerRow.getCell(i + 1);
        cell.value = header;
        cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFDC143C' }
        };
        cell.alignment = { horizontal: 'center', vertical: 'middle' };
      });
      
      let currentRow = 9;
      data.forEach((item) => {
        const row = worksheet.getRow(currentRow);
        row.getCell(1).value = item.itemCode;
        row.getCell(2).value = isRTL ? item.itemNameAr : item.itemName;
        row.getCell(3).value = item.batchNumber || '-';
        row.getCell(4).value = formatDate(item.expiryDate);
        row.getCell(5).value = item.daysRemaining;
        row.getCell(6).value = item.quantity;
        row.getCell(7).value = item.unitCost;
        row.getCell(8).value = item.totalValue;
        row.getCell(9).value = item.warehouseName;
        row.getCell(10).value = getStatusLabel(item.status);
        row.getCell(11).value = getRecommendation(item.daysRemaining);
        
        // Color coding
        const color = item.status === 'expired' || item.status === 'critical' ? 'FFFFE0E0' : 
                     item.status === 'warning' ? 'FFFFF4E0' : 'FFE0FFE0';
        row.eachCell(cell => {
          cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: color }
          };
        });
        
        currentRow++;
      });

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ExpiryReport_${new Date().getTime()}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      toast.success(isRTL ? 'تم التصدير بنجاح ✅' : 'Exported successfully ✅');
    } catch (error) {
      console.error('Export failed:', error);
      toast.error(isRTL ? 'فشل التصدير ❌' : 'Export failed ❌');
    }
  };

  const exportToPdf = async () => {
    try {
      const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
      const pageWidth = doc.internal.pageSize.width;

      doc.setFillColor(255, 240, 240);
      doc.rect(0, 0, pageWidth, 45, 'F');

      if (companyInfo?.logoBase64) {
        try {
          doc.addImage(companyInfo.logoBase64, 'PNG', 12, 8, 30, 25);
        } catch (err) {
          console.warn('Could not add logo:', err);
        }
      }

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(18);
      doc.setTextColor(220, 20, 60);
      doc.text(isRTL ? (companyInfo?.companyNameAr || '') : (companyInfo?.companyName || ''), pageWidth / 2, 15, { align: 'center' });

      doc.setFillColor(220, 20, 60);
      doc.roundedRect(pageWidth / 2 - 60, 32, 120, 10, 3, 3, 'F');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(14);
      doc.setTextColor(255, 255, 255);
      doc.text(isRTL ? t.titleAr : t.title, pageWidth / 2, 38.5, { align: 'center' });

      const tableHeaders = [
        t.itemName,
        t.batchNumber,
        t.expiryDate,
        t.daysRemaining,
        t.quantity,
        t.totalValue,
        t.status,
      ];

      const tableData: any[] = [];
      const data = getFilteredItems();

      data.forEach((item) => {
        tableData.push([
          (isRTL ? item.itemNameAr : item.itemName).substring(0, 20),
          item.batchNumber || '-',
          formatDate(item.expiryDate),
          item.daysRemaining.toString(),
          formatCurrency(item.quantity),
          formatCurrency(item.totalValue),
          getStatusLabel(item.status),
        ]);
      });

      autoTable(doc, {
        head: [tableHeaders],
        body: tableData,
        startY: 50,
        theme: 'grid',
        headStyles: {
          fillColor: [220, 20, 60],
          textColor: [255, 255, 255],
          fontSize: 9,
          fontStyle: 'bold',
          halign: 'center'
        },
        bodyStyles: {
          fontSize: 8,
          textColor: [50, 50, 50]
        },
        didParseCell: function(data) {
          if (data.section === 'body' && data.row.index < tableData.length) {
            const item = getFilteredItems()[data.row.index];
            if (item.status === 'expired' || item.status === 'critical') {
              data.cell.styles.fillColor = [255, 224, 224];
            } else if (item.status === 'warning') {
              data.cell.styles.fillColor = [255, 244, 224];
            }
          }
        },
      });

      doc.save(`ExpiryReport_${new Date().toISOString().split('T')[0]}.pdf`);
      toast.success(isRTL ? 'تم التصدير بنجاح ✅' : 'Exported successfully ✅');
    } catch (error) {
      console.error('PDF export error:', error);
      toast.error(isRTL ? 'فشل التصدير ❌' : 'Export failed ❌');
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const stats = getStatistics();

  return (
    <Box sx={{ p: 3 }} dir={isRTL ? 'rtl' : 'ltr'}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ 
          background: 'linear-gradient(45deg, #DC143C 30%, #FF6347 90%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          fontWeight: 'bold'
        }}>
          {t.title}
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 1 }} className="no-print">
          <Tooltip title={t.refresh}>
            <IconButton onClick={loadData} color="error">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button variant="contained" startIcon={<ExcelIcon />} onClick={exportToExcel}
            sx={{ background: 'linear-gradient(45deg, #4CAF50 30%, #66BB6A 90%)', color: 'white' }}>
            {t.exportExcel}
          </Button>
          <Button variant="contained" startIcon={<PdfIcon />} onClick={exportToPdf}
            sx={{ background: 'linear-gradient(45deg, #f44336 30%, #e57373 90%)', color: 'white' }}>
            {t.exportPdf}
          </Button>
          <Button variant="contained" startIcon={<PrintIcon />} onClick={handlePrint}
            sx={{ background: 'linear-gradient(45deg, #FF9800 30%, #FFB74D 90%)', color: 'white' }}>
            {t.print}
          </Button>
        </Box>
      </Box>

      {stats.expiredCount > 0 && (
        <Alert severity="error" icon={<ErrorIcon />} sx={{ mb: 3 }} className="no-print">
          <strong>{stats.expiredCount} {t.itemsCount}</strong> {t.expired} - {t.totalValue}: {formatCurrency(stats.expiredValue)} KWD
        </Alert>
      )}

      {stats.nearExpiryCount > 0 && (
        <Alert severity="warning" icon={<WarningIcon />} sx={{ mb: 3 }} className="no-print">
          <strong>{stats.nearExpiryCount} {t.itemsCount}</strong> {t.nearExpiry} - {t.totalValue}: {formatCurrency(stats.nearExpiryValue)} KWD
        </Alert>
      )}

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 2, mb: 3 }} className="no-print">
        <Card sx={{ background: 'linear-gradient(135deg, #DC143C 0%, #FF6347 100%)', color: 'white', transition: 'transform 0.3s', '&:hover': { transform: 'translateY(-5px)' } }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>{t.expiredCount}</Typography>
                <Typography variant="h4" sx={{ fontWeight: 'bold', mt: 1 }}>{stats.expiredCount}</Typography>
              </Box>
              <ExpiredIcon sx={{ fontSize: 48, opacity: 0.3 }} />
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ background: 'linear-gradient(135deg, #FF8C00 0%, #FFA500 100%)', color: 'white', transition: 'transform 0.3s', '&:hover': { transform: 'translateY(-5px)' } }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>{t.expiredValue}</Typography>
                <Typography variant="h4" sx={{ fontWeight: 'bold', mt: 1 }}>{formatCurrency(stats.expiredValue)}</Typography>
              </Box>
              <ErrorIcon sx={{ fontSize: 48, opacity: 0.3 }} />
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)', color: 'white', transition: 'transform 0.3s', '&:hover': { transform: 'translateY(-5px)' } }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>{t.nearExpiryCount}</Typography>
                <Typography variant="h4" sx={{ fontWeight: 'bold', mt: 1 }}>{stats.nearExpiryCount}</Typography>
              </Box>
              <WarningIcon sx={{ fontSize: 48, opacity: 0.3 }} />
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ background: 'linear-gradient(135deg, #8B0000 0%, #DC143C 100%)', color: 'white', transition: 'transform 0.3s', '&:hover': { transform: 'translateY(-5px)' } }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>{t.lossPercentage}</Typography>
                <Typography variant="h4" sx={{ fontWeight: 'bold', mt: 1 }}>{stats.lossPercentage.toFixed(2)}%</Typography>
              </Box>
              <ErrorIcon sx={{ fontSize: 48, opacity: 0.3 }} />
            </Box>
            <LinearProgress variant="determinate" value={stats.lossPercentage > 100 ? 100 : stats.lossPercentage} 
              sx={{ mt: 2, bgcolor: 'rgba(255,255,255,0.3)', '& .MuiLinearProgress-bar': { bgcolor: 'white' } }} />
          </CardContent>
        </Card>
      </Box>

      <Paper sx={{ p: 3, mb: 3 }} className="no-print">
        <Typography variant="h6" sx={{ mb: 2, color: 'error.main' }}>Filters</Typography>
        
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 2 }}>
          <Autocomplete
            size="small"
            options={[{ id: '' as number | '', label: t.allWarehouses }, ...warehouses.map(wh => ({ id: wh.warehouseId as number | '', label: isRTL ? wh.warehouseNameAr || wh.warehouseName : wh.warehouseName }))]}
            getOptionLabel={(o) => o.label || ''}
            value={[{ id: '' as number | '', label: t.allWarehouses }, ...warehouses.map(wh => ({ id: wh.warehouseId as number | '', label: isRTL ? wh.warehouseNameAr || wh.warehouseName : wh.warehouseName }))].find(o => o.id === warehouseFilter) || { id: '' as number | '', label: t.allWarehouses }}
            onChange={(_, v) => { setWarehouseFilter(v?.id || ''); setDetailPage(0); }}
            renderInput={(params) => <TextField {...params} label={t.warehouse} />}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            fullWidth
          />

          <TextField select fullWidth size="small" label={t.expiryStatus} value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setDetailPage(0); }}>
            <MenuItem value="">{t.allStatuses}</MenuItem>
            <MenuItem value="expired">{t.expired}</MenuItem>
            <MenuItem value="critical">{t.critical}</MenuItem>
            <MenuItem value="warning">{t.warning}</MenuItem>
            <MenuItem value="safe">{t.safe}</MenuItem>
          </TextField>
        </Box>
      </Paper>

      <Paper sx={{ mb: 2 }} className="no-print">
        <Tabs value={currentTab} onChange={(_, newValue) => { setCurrentTab(newValue); setDetailPage(0); }} variant="scrollable" scrollButtons="auto">
          <Tab label={t.overview} />
          <Tab label={t.expiredItems} />
          <Tab label={t.nearExpiry} />
          <Tab label={t.byWarehouse} />
        </Tabs>
      </Paper>

      <Box className="print-only">
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, pb: 1, borderBottom: '2px solid #DC143C' }}>
          {companyInfo?.logoBase64 && (
            <Box><img src={companyInfo.logoBase64} alt="Logo" style={{ height: '40px', maxWidth: '100px' }} /></Box>
          )}
          <Box sx={{ textAlign: 'center', flex: 1 }}>
            <Typography sx={{ fontSize: '20px', fontWeight: 'bold', color: '#DC143C', mb: 0.3 }}>
              {isRTL ? companyInfo?.companyNameAr : companyInfo?.companyName}
            </Typography>
            <Typography sx={{ fontSize: '9px', color: '#546E7A' }}>{companyInfo?.address || ''} | 📞 {companyInfo?.phone || ''}</Typography>
          </Box>
          <Box sx={{ width: '100px' }}></Box>
        </Box>

        <Box sx={{ textAlign: 'center', mb: 1, py: 0.5, background: 'linear-gradient(90deg, #DC143C 0%, #FF6347 100%)', color: 'white' }}>
          <Typography sx={{ fontSize: '13px', fontWeight: 'bold' }}>{isRTL ? t.titleAr : t.title}</Typography>
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}><CircularProgress color="error" /></Box>
      ) : (
        <TableContainer component={Paper}>
          {currentTab === 0 && (
            <>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow sx={{ background: 'linear-gradient(45deg, #DC143C 30%, #FF6347 90%)' }}>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t.itemName}</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t.batchNumber}</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t.expiryDate}</TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>{t.daysRemaining}</TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>{t.quantity}</TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>{t.totalValue}</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t.warehouseName}</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t.status}</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t.recommendation}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {getFilteredItems().slice(detailPage * detailRowsPerPage, detailPage * detailRowsPerPage + detailRowsPerPage).map((row, index) => (
                  <TableRow key={index} sx={{ 
                    '&:hover': { backgroundColor: 'action.hover' },
                    bgcolor: row.status === 'expired' || row.status === 'critical' ? '#ffe0e0' : 
                            row.status === 'warning' ? '#fff4e0' : 'inherit'
                  }}>
                    <TableCell>
                      <Typography variant="body2" fontWeight="500">{isRTL ? row.itemNameAr : row.itemName}</Typography>
                      <Typography variant="caption" color="text.secondary">{row.itemCode}</Typography>
                    </TableCell>
                    <TableCell>{row.batchNumber || '-'}</TableCell>
                    <TableCell>{formatDate(row.expiryDate)}</TableCell>
                    <TableCell align="right">
                      <Typography sx={{ 
                        color: row.daysRemaining < 0 ? 'error.main' : 
                               row.daysRemaining <= 7 ? 'error.main' :
                               row.daysRemaining <= 30 ? 'warning.main' : 'success.main',
                        fontWeight: 'bold'
                      }}>
                        {row.daysRemaining}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">{formatCurrency(row.quantity)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>{formatCurrency(row.totalValue)}</TableCell>
                    <TableCell>{row.warehouseName}</TableCell>
                    <TableCell>
                      <Chip label={getStatusLabel(row.status)} size="small" color={getStatusColor(row.status)} />
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" sx={{ 
                        color: row.daysRemaining < 0 ? 'error.main' : 
                               row.daysRemaining <= 7 ? 'error.main' :
                               row.daysRemaining <= 30 ? 'warning.main' : 'success.main',
                        fontWeight: 'bold'
                      }}>
                        {getRecommendation(row.daysRemaining)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
                {getFilteredItems().length === 0 && (
                  <TableRow><TableCell colSpan={9} align="center" sx={{ py: 5 }}><Typography color="text.secondary">{t.noData}</Typography></TableCell></TableRow>
                )}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={getFilteredItems().length}
              page={detailPage}
              onPageChange={(_, p) => setDetailPage(p)}
              rowsPerPage={detailRowsPerPage}
              onRowsPerPageChange={(e) => { setDetailRowsPerPage(parseInt(e.target.value, 10)); setDetailPage(0); }}
              rowsPerPageOptions={[10, 25, 50, 100]}
            />
            </>
          )}

          {currentTab === 1 && (
            <>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow sx={{ background: 'linear-gradient(45deg, #DC143C 30%, #FF6347 90%)' }}>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t.itemName}</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t.batchNumber}</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t.expiryDate}</TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>{t.daysRemaining}</TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>{t.quantity}</TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>{t.totalValue}</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t.recommendation}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {getExpiredItems().slice(detailPage * detailRowsPerPage, detailPage * detailRowsPerPage + detailRowsPerPage).map((row, index) => (
                  <TableRow key={index} sx={{ bgcolor: '#ffe0e0' }}>
                    <TableCell>
                      <Typography variant="body2" fontWeight="500">{isRTL ? row.itemNameAr : row.itemName}</Typography>
                      <Typography variant="caption" color="text.secondary">{row.itemCode}</Typography>
                    </TableCell>
                    <TableCell>{row.batchNumber || '-'}</TableCell>
                    <TableCell>{formatDate(row.expiryDate)}</TableCell>
                    <TableCell align="right">
                      <Typography sx={{ color: 'error.main', fontWeight: 'bold' }}>
                        {row.daysRemaining}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">{formatCurrency(row.quantity)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', color: 'error.main' }}>{formatCurrency(row.totalValue)}</TableCell>
                    <TableCell>
                      <Typography variant="caption" sx={{ color: 'error.main', fontWeight: 'bold' }}>
                        {getRecommendation(row.daysRemaining)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={getExpiredItems().length}
              page={detailPage}
              onPageChange={(_, p) => setDetailPage(p)}
              rowsPerPage={detailRowsPerPage}
              onRowsPerPageChange={(e) => { setDetailRowsPerPage(parseInt(e.target.value, 10)); setDetailPage(0); }}
              rowsPerPageOptions={[10, 25, 50, 100]}
            />
            </>
          )}

          {currentTab === 2 && (
            <>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow sx={{ background: 'linear-gradient(45deg, #FF8C00 30%, #FFA500 90%)' }}>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t.itemName}</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t.batchNumber}</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t.expiryDate}</TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>{t.daysRemaining}</TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>{t.quantity}</TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>{t.totalValue}</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t.recommendation}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {getNearExpiryItems().slice(detailPage * detailRowsPerPage, detailPage * detailRowsPerPage + detailRowsPerPage).map((row, index) => (
                  <TableRow key={index} sx={{ bgcolor: row.status === 'critical' ? '#ffe0e0' : '#fff4e0' }}>
                    <TableCell>
                      <Typography variant="body2" fontWeight="500">{isRTL ? row.itemNameAr : row.itemName}</Typography>
                      <Typography variant="caption" color="text.secondary">{row.itemCode}</Typography>
                    </TableCell>
                    <TableCell>{row.batchNumber || '-'}</TableCell>
                    <TableCell>{formatDate(row.expiryDate)}</TableCell>
                    <TableCell align="right">
                      <Typography sx={{ 
                        color: row.status === 'critical' ? 'error.main' : 'warning.main',
                        fontWeight: 'bold'
                      }}>
                        {row.daysRemaining}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">{formatCurrency(row.quantity)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>{formatCurrency(row.totalValue)}</TableCell>
                    <TableCell>
                      <Typography variant="caption" sx={{ 
                        color: row.status === 'critical' ? 'error.main' : 'warning.main',
                        fontWeight: 'bold'
                      }}>
                        {getRecommendation(row.daysRemaining)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={getNearExpiryItems().length}
              page={detailPage}
              onPageChange={(_, p) => setDetailPage(p)}
              rowsPerPage={detailRowsPerPage}
              onRowsPerPageChange={(e) => { setDetailRowsPerPage(parseInt(e.target.value, 10)); setDetailPage(0); }}
              rowsPerPageOptions={[10, 25, 50, 100]}
            />
            </>
          )}

          {currentTab === 3 && (
            <Table stickyHeader>
              <TableHead>
                <TableRow sx={{ background: 'linear-gradient(45deg, #DC143C 30%, #FF6347 90%)' }}>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t.warehouseName}</TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>{t.totalItems}</TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>{t.expiredCount}</TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>{t.expiredValue}</TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>{t.nearExpiryCount}</TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>{t.nearExpiryValue}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {getWarehouseSummary().map((row, index) => (
                  <TableRow key={index} sx={{ '&:hover': { backgroundColor: 'action.hover' } }}>
                    <TableCell sx={{ fontWeight: '500' }}>{isRTL ? row.warehouseNameAr : row.warehouseName}</TableCell>
                    <TableCell align="right">{row.totalItems}</TableCell>
                    <TableCell align="right" sx={{ color: 'error.main', fontWeight: 'bold' }}>{row.expiredItems}</TableCell>
                    <TableCell align="right" sx={{ color: 'error.main', fontWeight: 'bold' }}>{formatCurrency(row.expiredValue)}</TableCell>
                    <TableCell align="right" sx={{ color: 'warning.main', fontWeight: 'bold' }}>{row.nearExpiryItems}</TableCell>
                    <TableCell align="right" sx={{ color: 'warning.main', fontWeight: 'bold' }}>{formatCurrency(row.nearExpiryValue)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </TableContainer>
      )}

      <style>{`
        @media print {
          header, nav, .MuiAppBar-root, .MuiDrawer-root, .MuiToolbar-root { display: none !important; }
          .no-print { display: none !important; }
          .print-only { display: block !important; }
          @page { margin: 0.5cm 1cm 1.5cm 1cm; size: landscape; }
          * { orphans: 3; widows: 3; }
          table { page-break-after: avoid; }
          body { print-color-adjust: exact; -webkit-print-color-adjust: exact; font-size: 9px; }
          main, .MuiBox-root { margin: 0 !important; padding: 8px !important; max-width: 100% !important; }
          table thead tr { background: linear-gradient(135deg, #DC143C 0%, #FF6347 100%) !important; -webkit-print-color-adjust: exact; }
          table thead th { background: linear-gradient(135deg, #DC143C 0%, #FF6347 100%) !important; color: white !important; font-weight: bold !important; padding: 4px 6px !important; border: 1px solid #DC143C !important; font-size: 9px !important; -webkit-print-color-adjust: exact; }
          table tbody td { padding: 3px 6px !important; border: 1px solid #E3F2FD !important; color: #263238 !important; font-size: 8px !important; line-height: 1.2 !important; -webkit-print-color-adjust: exact; }
          table tbody td .MuiTypography-caption { color: #546E7A !important; opacity: 1 !important; }
          .MuiChip-root { height: 16px !important; font-size: 7px !important; -webkit-print-color-adjust: exact; }
          .MuiChip-colorError { background-color: #DC143C !important; color: white !important; -webkit-print-color-adjust: exact; }
          .MuiChip-colorWarning { background-color: #FF9800 !important; color: white !important; -webkit-print-color-adjust: exact; }
          .MuiChip-colorSuccess { background-color: #4CAF50 !important; color: white !important; -webkit-print-color-adjust: exact; }
          .MuiTableContainer-root { box-shadow: none !important; border-radius: 0 !important; overflow: visible !important; }
          .print-only h4, .print-only h3, .print-only h5 { color: #DC143C !important; -webkit-print-color-adjust: exact; }
          .print-only .MuiPaper-root { -webkit-print-color-adjust: exact; box-shadow: none !important; }
          .print-only img { -webkit-print-color-adjust: exact; }
        }
        @media screen { .print-only { display: none !important; } }
      `}</style>
    </Box>
  );
};

export default ExpiryReport;